import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  DollarSign, TrendingUp, Users, Award, Star, Zap,
  CheckCircle2, ArrowRight, ChevronLeft, Rocket, Target,
  Gift, Calendar, Clock, Mail, Phone, User, Briefcase,
  Heart, Shield, Globe
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Commission Examples
const COMMISSION_EXAMPLES = [
  {
    tier: 'Bronze',
    color: 'from-amber-500 to-amber-700',
    bgColor: 'bg-amber-50',
    establishment: 500,
    monthly: 500,
    startupCommission: 375,
    trailingMonthly: 75,
    yearlyTrailing: 900,
    retention12mo: 50,
    totalYear1: 1325,
  },
  {
    tier: 'Silver',
    color: 'from-slate-400 to-slate-600',
    bgColor: 'bg-slate-50',
    establishment: 1000,
    monthly: 1000,
    startupCommission: 750,
    trailingMonthly: 150,
    yearlyTrailing: 1800,
    retention12mo: 150,
    totalYear1: 2700,
    popular: true,
  },
  {
    tier: 'Gold',
    color: 'from-yellow-400 to-amber-500',
    bgColor: 'bg-yellow-50',
    establishment: 2500,
    monthly: 2500,
    startupCommission: 1875,
    trailingMonthly: 375,
    yearlyTrailing: 4500,
    retention12mo: 375,
    totalYear1: 6750,
  }
];

// Benefits
const BENEFITS = [
  {
    icon: DollarSign,
    title: '75% Startup Commission',
    description: 'Earn 75% of every establishment fee you close. Bronze = $375, Silver = $750, Gold = $1,875 per deal.'
  },
  {
    icon: TrendingUp,
    title: '15% Trailing Commission',
    description: 'Passive income every month for as long as your client stays subscribed. Build a recurring revenue stream.'
  },
  {
    icon: Gift,
    title: 'Retention Bonuses',
    description: '10% bonus at 6 months, 15% bonus at 12 months. Rewarding you for bringing quality, long-term partners.'
  },
  {
    icon: Target,
    title: 'No Earning Cap',
    description: 'The more you sell, the more you earn. Top performers can earn $10k+ per month in passive income alone.'
  },
  {
    icon: Users,
    title: 'Team Leadership Path',
    description: 'Recruit your own team and earn override commissions. Build your own sales empire within Puppoto.'
  },
  {
    icon: Shield,
    title: 'Full Support System',
    description: 'Sales training, marketing materials, CRM access, and dedicated support to help you succeed.'
  }
];

// Income Scenarios
const INCOME_SCENARIOS = [
  {
    title: 'Part-Time Hustler',
    deals: '2 Silver/month',
    monthly: '$1,500',
    yearly: '$18,000+',
    description: 'Perfect for side income while keeping your day job'
  },
  {
    title: 'Full-Time Closer',
    deals: '8 Mixed/month',
    monthly: '$6,000',
    yearly: '$72,000+',
    description: 'Dedicated sales professional building a book'
  },
  {
    title: 'Sales Leader',
    deals: '15+ Mixed/month + Team',
    monthly: '$15,000+',
    yearly: '$180,000+',
    description: 'Top performer with team overrides'
  }
];

const CommissionCard = ({ example }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    className={`relative rounded-3xl p-6 ${example.bgColor} border-2 ${
      example.popular ? 'border-[#FF7555] ring-2 ring-[#FF7555] ring-offset-2' : 'border-gray-200'
    }`}
  >
    {example.popular && (
      <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-[#FF7555] text-white text-sm font-semibold rounded-full">
        Most Common
      </div>
    )}
    
    <div className={`text-2xl font-bold bg-gradient-to-r ${example.color} bg-clip-text text-transparent mb-4`}>
      {example.tier} Partner
    </div>
    
    <div className="space-y-3 mb-6">
      <div className="flex justify-between items-center py-2 border-b border-gray-200">
        <span className="text-[#7A7571]">Startup Commission (75%)</span>
        <span className="font-bold text-green-600">${example.startupCommission}</span>
      </div>
      <div className="flex justify-between items-center py-2 border-b border-gray-200">
        <span className="text-[#7A7571]">Monthly Trailing (15%)</span>
        <span className="font-semibold text-[#1A1817]">${example.trailingMonthly}/mo</span>
      </div>
      <div className="flex justify-between items-center py-2 border-b border-gray-200">
        <span className="text-[#7A7571]">Year 1 Trailing Total</span>
        <span className="font-semibold text-[#1A1817]">${example.yearlyTrailing.toLocaleString()}</span>
      </div>
      <div className="flex justify-between items-center py-2 border-b border-gray-200">
        <span className="text-[#7A7571]">12-Month Retention Bonus</span>
        <span className="font-semibold text-purple-600">+${example.retention12mo}</span>
      </div>
    </div>
    
    <div className="bg-gradient-to-r from-[#FF7555] to-[#E5684A] rounded-2xl p-4 text-white text-center">
      <div className="text-sm opacity-90">Total Year 1 Earnings</div>
      <div className="text-3xl font-bold">${example.totalYear1.toLocaleString()}</div>
      <div className="text-sm opacity-90">per deal</div>
    </div>
  </motion.div>
);

const ApplicationForm = () => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    location: '',
    experience: '',
    linkedin: '',
    whyJoin: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error('Please fill in required fields');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/puppoto/sales-application`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      
      toast.success('Application submitted! We\'ll be in touch within 48 hours.');
      setForm({ name: '', email: '', phone: '', location: '', experience: '', linkedin: '', whyJoin: '' });
    } catch (error) {
      toast.success('Application received! Our team will contact you soon.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-[#1A1817] mb-1">Full Name *</label>
          <Input
            value={form.name}
            onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
            placeholder="John Smith"
            className="rounded-xl"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#1A1817] mb-1">Email *</label>
          <Input
            type="email"
            value={form.email}
            onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
            placeholder="john@email.com"
            className="rounded-xl"
          />
        </div>
      </div>
      
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-[#1A1817] mb-1">Phone *</label>
          <Input
            value={form.phone}
            onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
            placeholder="0400 000 000"
            className="rounded-xl"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#1A1817] mb-1">Location</label>
          <Input
            value={form.location}
            onChange={e => setForm(p => ({ ...p, location: e.target.value }))}
            placeholder="Sydney, NSW"
            className="rounded-xl"
          />
        </div>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-[#1A1817] mb-1">Sales Experience</label>
        <select
          value={form.experience}
          onChange={e => setForm(p => ({ ...p, experience: e.target.value }))}
          className="w-full h-10 px-3 rounded-xl border border-[#EBE8E0]"
        >
          <option value="">Select experience level...</option>
          <option value="none">New to sales</option>
          <option value="1-2">1-2 years</option>
          <option value="3-5">3-5 years</option>
          <option value="5+">5+ years</option>
          <option value="manager">Sales manager/leader</option>
        </select>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-[#1A1817] mb-1">LinkedIn Profile (optional)</label>
        <Input
          value={form.linkedin}
          onChange={e => setForm(p => ({ ...p, linkedin: e.target.value }))}
          placeholder="linkedin.com/in/yourprofile"
          className="rounded-xl"
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-[#1A1817] mb-1">Why do you want to join Puppoto?</label>
        <textarea
          value={form.whyJoin}
          onChange={e => setForm(p => ({ ...p, whyJoin: e.target.value }))}
          placeholder="Tell us about yourself and why you'd be a great fit..."
          className="w-full px-4 py-3 rounded-xl border border-[#EBE8E0] resize-none h-24"
        />
      </div>
      
      <Button
        type="submit"
        disabled={submitting}
        className="w-full h-12 bg-[#FF7555] hover:bg-[#E5684A] text-white rounded-xl font-semibold"
      >
        {submitting ? 'Submitting...' : 'Apply Now'}
        <ArrowRight className="w-4 h-4 ml-2" />
      </Button>
    </form>
  );
};

const JoinSalesTeam = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#FDFCF9]">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-[#EBE8E0] sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/puppoto')}
            className="flex items-center gap-2 text-[#1A1817] hover:text-[#FF7555]"
          >
            <ChevronLeft className="w-5 h-5" />
            <span className="font-medium">Back to Puppoto</span>
          </button>
          
          <div className="flex items-center gap-2">
            <span className="text-2xl">🐕</span>
            <span className="text-xl font-bold text-[#1A1817]" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Join Our Sales Team
            </span>
          </div>
          
          <Button
            onClick={() => document.getElementById('apply').scrollIntoView({ behavior: 'smooth' })}
            className="bg-[#FF7555] hover:bg-[#E5684A] text-white rounded-xl"
          >
            Apply Now
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 px-4 bg-gradient-to-br from-[#FF7555]/10 via-white to-[#4E876C]/10">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-medium mb-6">
              <Zap className="w-4 h-4" />
              Now Recruiting Sales Partners
            </span>
            
            <h1 className="text-4xl md:text-5xl font-bold text-[#1A1817] mb-6" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Earn Up To <span className="text-[#FF7555]">$6,750</span> Per Deal
            </h1>
            
            <p className="text-lg text-[#7A7571] mb-8 max-w-2xl mx-auto">
              Join Australia's fastest-growing dog platform sales team. 
              High commissions, recurring income, and unlimited earning potential.
            </p>

            <div className="flex flex-wrap justify-center gap-4">
              <div className="flex items-center gap-2 text-[#1A1817]">
                <CheckCircle2 className="w-5 h-5 text-green-500" />
                <span>75% Startup Commission</span>
              </div>
              <div className="flex items-center gap-2 text-[#1A1817]">
                <CheckCircle2 className="w-5 h-5 text-green-500" />
                <span>15% Monthly Trailing</span>
              </div>
              <div className="flex items-center gap-2 text-[#1A1817]">
                <CheckCircle2 className="w-5 h-5 text-green-500" />
                <span>Retention Bonuses</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Commission Breakdown */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#1A1817] mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Your Commission Structure
            </h2>
            <p className="text-[#7A7571] max-w-xl mx-auto">
              Transparent earnings. No caps. The more you close, the more you earn.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {COMMISSION_EXAMPLES.map((example, idx) => (
              <CommissionCard key={idx} example={example} />
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#1A1817] mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              How You Earn
            </h2>
          </div>
          
          <div className="space-y-6">
            {[
              {
                step: 1,
                title: 'Close a Deal',
                description: 'Sign a new sponsor to Puppoto. We provide leads, training, and marketing materials.',
                icon: Target
              },
              {
                step: 2,
                title: 'Earn Startup Commission',
                description: '75% of the establishment fee hits your account. Bronze = $375, Silver = $750, Gold = $1,875.',
                icon: DollarSign
              },
              {
                step: 3,
                title: 'Build Recurring Income',
                description: 'Every month your client stays, you earn 15% of their subscription. Passive income that grows.',
                icon: TrendingUp
              },
              {
                step: 4,
                title: 'Earn Retention Bonuses',
                description: 'When clients hit 6 and 12 months, you get bonus payouts. Quality clients = more money.',
                icon: Gift
              }
            ].map((item, idx) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="flex gap-4 items-start"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#FF7555] flex items-center justify-center flex-shrink-0">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm text-[#FF7555] font-medium">Step {item.step}</span>
                    </div>
                    <h3 className="text-lg font-bold text-[#1A1817]">{item.title}</h3>
                    <p className="text-[#7A7571]">{item.description}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Income Scenarios */}
      <section className="py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#1A1817] mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Real Earning Potential
            </h2>
            <p className="text-[#7A7571]">What top performers are earning</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {INCOME_SCENARIOS.map((scenario, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white rounded-2xl p-6 border border-[#EBE8E0]"
              >
                <h3 className="text-xl font-bold text-[#1A1817] mb-2">{scenario.title}</h3>
                <p className="text-sm text-[#7A7571] mb-4">{scenario.description}</p>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-[#7A7571]">Activity</span>
                    <span className="font-medium">{scenario.deals}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#7A7571]">Monthly Income</span>
                    <span className="font-bold text-green-600">{scenario.monthly}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-[#EBE8E0]">
                    <span className="text-[#7A7571]">Yearly Potential</span>
                    <span className="font-bold text-[#FF7555] text-lg">{scenario.yearly}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#1A1817] mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Why Join Puppoto Sales?
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {BENEFITS.map((benefit, idx) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.05 }}
                  className="p-5"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#FF7555]/10 flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-[#FF7555]" />
                  </div>
                  <h3 className="text-lg font-bold text-[#1A1817] mb-2">{benefit.title}</h3>
                  <p className="text-sm text-[#7A7571]">{benefit.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section id="apply" className="py-16 px-4 bg-gradient-to-br from-[#FF7555]/5 to-[#4E876C]/5">
        <div className="max-w-xl mx-auto">
          <div className="bg-white rounded-3xl p-8 shadow-lg border border-[#EBE8E0]">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-gradient-to-br from-[#FF7555] to-[#E5684A] rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Rocket className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-[#1A1817]" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                Apply to Join
              </h2>
              <p className="text-[#7A7571] mt-2">Start earning with Puppoto</p>
            </div>
            
            <ApplicationForm />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-[#EBE8E0] bg-white">
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-sm text-[#7A7571]">
            © 2026 Puppoto Sales Team • Part of the Lionheart Digital family
          </p>
        </div>
      </footer>
    </div>
  );
};

export default JoinSalesTeam;
